// berryWeighing — shared math for OPTIONAL weight capture at berry intake
// (BlueberryDeliveries). A delivery carries zero-or-more weigh groups, each a
// count of crates + their gross weight; net = gross − crate_count × per-crate
// tare. The "average per delivery" case is ONE group spanning all the crates;
// the "weighed" case is several groups (a pallet/dolly weighed at a time).
//
// This is the same gross-minus-tare math the cooling batch / tipping already
// use (src/lib/tippingMath.ts, supabase mig 808 / 1045) so receiving and
// Precool agree on how a berry crate's tare is deducted. "Gross, auto-deduct
// tare" is the chosen basis — the operator enters gross; net is derived.

import { CONTAINER_LABEL, type ContainerType } from '@/lib/containerTypes'

// Placeholder per-crate tare when a real one isn't configured in Bin Types.
// A deliberate non-zero value (a bare 0 would silently deduct nothing and
// over-weigh every load). MUST match Precool's PLACEHOLDER_CRATE_TARE_KG so the
// two stages behave identically until the operator sets the real crate weight.
export const PLACEHOLDER_CRATE_TARE_KG = 1.0

// Soft plausibility ceiling for an AVERAGE-mode entry: berry crates are small,
// hand-carried containers, so a per-crate FRUIT weight above this almost always
// means the operator typed the whole load's weight (or a 10× fat-finger) into
// the per-crate field. Used only for a non-blocking on-screen nudge — never a
// hard block, since we can't know a given site's crate size for certain.
export const PLAUSIBLE_MAX_FRUIT_PER_CRATE_KG = 25

export interface WeighGroupInput {
  crateCount: number
  grossKg:    number
}

// Net fruit weight for one weigh group: gross − crates × tare. Returns null
// when the inputs don't (yet) make a valid POSITIVE net — so callers show a
// dash and block submit rather than persisting a negative/zero net (which the
// DB CHECK would reject anyway).
export function weighGroupNetKg(grossKg: number, crateCount: number, tareKg: number): number | null {
  if (!(grossKg > 0) || !Number.isFinite(crateCount) || crateCount <= 0 || !(tareKg >= 0)) return null
  const net = grossKg - crateCount * tareKg
  return net > 0 ? net : null
}

export interface WeighTotals {
  gross:          number
  net:            number
  crates:         number
  avgNetPerCrate: number | null
  // How many groups the operator has actually started filling in.
  groupsWithInput: number
  // A started group whose net isn't a valid positive number (e.g. gross below
  // total tare). Blocks submit; empty groups are ignored, not invalid.
  hasInvalid:      boolean
}

// Fold weigh groups into delivery totals. Empty groups (no gross and no count)
// are skipped so a stray blank row never blocks a save; a *partially* filled
// group counts as input and, if it can't make a positive net, flags hasInvalid.
export function weighTotals(groups: WeighGroupInput[], tareKg: number): WeighTotals {
  let gross = 0, net = 0, crates = 0, groupsWithInput = 0, hasInvalid = false
  for (const g of groups) {
    const started = g.grossKg > 0 || g.crateCount > 0
    if (!started) continue
    groupsWithInput++
    const n = weighGroupNetKg(g.grossKg, g.crateCount, tareKg)
    if (n == null) { hasInvalid = true; continue }
    gross  += g.grossKg
    net    += n
    crates += g.crateCount
  }
  return {
    gross, net, crates,
    avgNetPerCrate: crates > 0 ? net / crates : null,
    groupsWithInput,
    hasInvalid,
  }
}

// Average-per-crate convenience: collapse an "avg gross per crate × N crates"
// entry into the SAME single weigh group the group-mode pipeline consumes, so
// net stays derived by weighGroupNetKg (gross − N×tare) and the stored gross is
// exactly the figure the readout showed. Gross is rounded to 0.1 kg (the Gross
// field's step) so the on-screen net and the DB-generated net can't drift on a
// non-round average.
//
// A real weighing needs BOTH a positive per-crate weight AND a positive count;
// if either is blank / NaN / non-positive it collapses to { 0, 0 } so weighTotals
// scores it "not started". This is what keeps weight OPTIONAL: the average form
// pre-fills the crate count from the delivery, and a pre-filled count must NOT on
// its own force a weight onto a count-only receive. (A both-positive but tiny
// average whose gross rounds to 0 returns { c, 0 }; weighGroupNetKg then rejects
// it, so the save is blocked with a clear message rather than silently dropped.)
export function avgModeToGroup(avgGrossPerCrate: number, crateCount: number): WeighGroupInput {
  const c = Number.isFinite(crateCount) && crateCount > 0 ? Math.trunc(crateCount) : 0
  const a = Number.isFinite(avgGrossPerCrate) && avgGrossPerCrate > 0 ? avgGrossPerCrate : 0
  if (!(a > 0 && c > 0)) return { crateCount: 0, grossKg: 0 }
  return { crateCount: c, grossKg: Math.round(a * c * 10) / 10 }
}

// ---------------------------------------------------------------------------
// Size split (mig 20260813100000)
// ---------------------------------------------------------------------------
// A sorted berry load arrives already graded: N crates of Small, N of Medium,
// N of Large, plus some reject crates. Each of those is ONE intake line, and a
// line may carry a weight or not — so the split works on a scale-less gate as a
// pure count, and doubles as the weigh capture when there IS a scale.
//
// The two invariants that make the numbers safe to trust:
//   · FRUIT crates (non-reject) are what the delivery is received as — the
//     intake form derives bins_received from them, so the header count and the
//     split can never disagree.
//   · REJECT crates are recorded but excluded from that total (decision,
//     2026-08-12), so Precool never offers a reject crate to be cooled and no
//     grower is paid on one.

export interface SizeLineInput {
  // Registry class code ("Small"), or null for the reject line.
  sizeClass: string | null
  isReject:  boolean
  crates:    number
  // 0 = not weighed. Weight stays optional per line, exactly as it is for the
  // delivery as a whole.
  grossKg:   number
}

export interface SizeSplitTotals {
  fruitCrates:  number
  rejectCrates: number
  fruitNet:     number
  rejectNet:    number
  fruitGross:   number
  // Lines the operator has actually started filling in.
  linesWithInput: number
  // Of those, how many carry a weight (drives "is this a weighed delivery").
  weighedLines:   number
  // FRUIT crates on lines that carry a weight. `fruitGross` is the gross of
  // exactly these crates, so this is what says whether that gross covers the
  // whole delivery or only part of it (L51).
  fruitCratesWeighed: number
  // A started line that can't be persisted: no crate count, or a gross that
  // doesn't clear the crates' tare. Blocks submit.
  hasInvalid:     boolean
}

// What one line contributes, or why it contributes nothing. Splitting this out
// of the accumulator keeps each half readable: this decides, the loop adds up.
export type SizeLineFold =
  | { kind: 'untouched' }
  | { kind: 'invalid' }
  | { kind: 'counted'; crates: number; net: number; gross: number; weighed: boolean }

export function foldSizeLine(l: SizeLineInput, tareKg: number): SizeLineFold {
  if (!(l.crates > 0 || l.grossKg > 0)) return { kind: 'untouched' }
  // A weight with no crate count can't be stored (crate_count > 0 is a DB
  // CHECK) and can't make a net — flag it rather than silently dropping it.
  if (!(l.crates > 0)) return { kind: 'invalid' }
  if (!(l.grossKg > 0)) {
    return { kind: 'counted', crates: l.crates, net: 0, gross: 0, weighed: false }
  }
  const net = weighGroupNetKg(l.grossKg, l.crates, tareKg)
  if (net == null) return { kind: 'invalid' }
  return { kind: 'counted', crates: l.crates, net, gross: l.grossKg, weighed: true }
}

// Fold size lines into delivery totals. Untouched lines are skipped — a size
// the load simply didn't contain is left blank, not zeroed.
export function sizeSplitTotals(lines: SizeLineInput[], tareKg: number): SizeSplitTotals {
  const t: SizeSplitTotals = {
    fruitCrates: 0, rejectCrates: 0, fruitNet: 0, rejectNet: 0, fruitGross: 0,
    linesWithInput: 0, weighedLines: 0, fruitCratesWeighed: 0, hasInvalid: false,
  }
  for (const l of lines) {
    const f = foldSizeLine(l, tareKg)
    if (f.kind === 'untouched') continue
    t.linesWithInput++
    if (f.kind === 'invalid') { t.hasInvalid = true; continue }
    if (f.weighed) t.weighedLines++
    if (l.isReject) {
      t.rejectCrates += f.crates
      t.rejectNet    += f.net
    } else {
      t.fruitCrates += f.crates
      t.fruitNet    += f.net
      t.fruitGross  += f.gross
      if (f.weighed) t.fruitCratesWeighed += f.crates
    }
  }
  return t
}

// Form state (a Record keyed by class code, values still strings) → the typed
// lines the maths and the RPC payload consume. Lives here rather than in the
// intake form so it is unit-testable without rendering the page.
export function buildSizeLines(
  sizeCodes: string[],
  counts: Record<string, { crates: string; grossKg: string }>,
  rejectKey: string,
): SizeLineInput[] {
  const at = (key: string) => counts[key] ?? { crates: '', grossKg: '' }
  const num = (v: string, int: boolean) => (int ? parseInt(v, 10) : parseFloat(v)) || 0
  const line = (key: string, sizeClass: string | null, isReject: boolean): SizeLineInput => ({
    sizeClass, isReject,
    crates:  num(at(key).crates, true),
    grossKg: num(at(key).grossKg, false),
  })
  return [
    ...sizeCodes.map(code => line(code, code, false)),
    line(rejectKey, null, true),
  ]
}

export interface IntakeLinePayload {
  crate_type:      string
  crate_count:     number
  gross_weight_kg: number | null
  crate_tare_kg:   number
  note:            string | null
  size_class:      string | null
  is_reject:       boolean
}

// Size lines → the receive_berry_delivery p_weighings payload. Untouched lines
// are dropped; an unweighed line sends a NULL gross (the RPC NULLIFs it) so it
// stores as a count-only intake line rather than a zero-weight one.
export function sizeLinesToPayload(
  lines: SizeLineInput[], crateType: string, tareKg: number,
): IntakeLinePayload[] {
  return lines
    .filter(l => l.crates > 0)
    .map(l => ({
      crate_type:      crateType,
      crate_count:     l.crates,
      gross_weight_kg: l.grossKg > 0 ? l.grossKg : null,
      crate_tare_kg:   tareKg,
      note:            null,
      size_class:      l.isReject ? null : l.sizeClass,
      is_reject:       l.isReject,
    }))
}

// One row of delivery_weighing_summary's size_breakdown JSON.
export interface SizeBreakdownRow {
  size_class: string | null
  is_reject:  boolean
  crates:     number
  net_kg:     number
}

// Order a breakdown for display: registry order first (Small before Large, not
// alphabetical — "Large" would otherwise sort ahead of "Small"), then any class
// no longer in the registry, then the reject row last. `order` is the tenant's
// active class codes in their configured sort_order.
export function sortSizeBreakdown(rows: SizeBreakdownRow[], order: string[]): SizeBreakdownRow[] {
  const rank = new Map(order.map((code, i) => [code, i]))
  // [rejects-last, configured position, name] — computed once per row so the
  // comparator is three plain comparisons instead of a branch tree.
  const key = (r: SizeBreakdownRow): [number, number, string] => [
    r.is_reject ? 1 : 0,
    rank.get(r.size_class ?? '') ?? Number.MAX_SAFE_INTEGER,
    r.size_class ?? '',
  ]
  return [...rows]
    .map(r => ({ r, k: key(r) }))
    .sort((a, b) => (a.k[0] - b.k[0]) || (a.k[1] - b.k[1]) || a.k[2].localeCompare(b.k[2]))
    .map(x => x.r)
}

// ---------------------------------------------------------------------------
// Printed-report totals
// ---------------------------------------------------------------------------
// The deliveries report's footer row and KPI strip. Pure, so the arithmetic a
// grower may query is unit-tested rather than only exercised by rendering.

export interface ReportDeliverySummary {
  netKg:   number
  perSize: Record<string, number>
  rejects: number
}

export interface ReportDeliveryRow {
  binsReceived:  number
  binsRemaining: number
  summary?:      ReportDeliverySummary
}

export interface ReportTotals {
  count:    number
  crates:   number
  uncooled: number
  batched:  number
  netKg:    number
  rejects:  number
  perSize:  Record<string, number>
}

export function foldReportTotals(rows: ReportDeliveryRow[]): ReportTotals {
  const t: ReportTotals = {
    count: rows.length, crates: 0, uncooled: 0, batched: 0,
    netKg: 0, rejects: 0, perSize: {},
  }
  for (const r of rows) {
    t.crates   += r.binsReceived
    t.uncooled += r.binsRemaining
    t.netKg    += r.summary?.netKg   ?? 0
    t.rejects  += r.summary?.rejects ?? 0
    for (const [code, n] of Object.entries(r.summary?.perSize ?? {})) {
      t.perSize[code] = (t.perSize[code] ?? 0) + n
    }
  }
  t.batched = t.crates - t.uncooled
  return t
}

// ---------------------------------------------------------------------------
// Printed-report CSV projection
// ---------------------------------------------------------------------------
// The spreadsheet a grower or auditor gets handed instead of the paper sheet.
// It lives beside foldReportTotals, and is pure for the same reason: these
// cells carry the crate counts and net kg a grower is paid on, so they are
// unit-tested rather than only exercised by clicking Download.
//
// Two rules the tests pin, both of which cost money if they drift:
//   • A field that was never captured exports EMPTY, never 0 — a delivery
//     received count-only has no net weight, and a "0.0" in that column reads
//     as "weighed, and it weighed nothing".
//   • The optional column blocks (delivery point, per-size split, rejects)
//     appear in the file exactly when they appear on paper, so the CSV and the
//     printout of the same filters have the same shape.

export const BERRY_DELIVERY_STATUS_LABELS: Record<string, string> = {
  RECEIVED: 'Received',
  IN_STOCK: 'In stock',
  TIPPED:   'Fully batched',   // berry: TIPPED = all crates drafted into cooling batches (mig 823)
}

/** The delivery fields the sheet prints. Structural, so the page owns its own row type. */
export interface ReportCsvDelivery {
  received_at:          string
  grower:               string
  variety:              string
  puc:                  string | null
  delivery_point:       string | null
  fruit_container_type: ContainerType | null
  delivery_type:        string
  vehicle_reg:          string | null
  driver_name:          string | null
  status:               string
  bins_received:        number
  bins_remaining:       number
}

/** Which optional column blocks this sheet carries — mirrors the printed table. */
export interface ReportCsvShape {
  showPoint: boolean
  sizeCols:  string[]
  showSizes: boolean
  /** An "Est. net kg" column (crate-weight-sample estimates). Optional: absent = off. */
  showEst?:  boolean
}

// Cell helpers. Each keeps its own null-handling so the row builders stay flat
// (and under the complexity gate) — an unrecorded value is '' in every case.
const csvText   = (v: string | null | undefined): string => v ?? ''
const csvStatus = (s: string): string => BERRY_DELIVERY_STATUS_LABELS[s] ?? s
const csvCrate  = (t: ContainerType | null): string => (t ? CONTAINER_LABEL[t] : '')
const csvSize   = (sm: ReportDeliverySummary | undefined, code: string): number | string =>
  sm?.perSize[code] ?? ''
// No summary row at all = nothing was split, so rejects are unknown, not zero.
const csvReject = (sm: ReportDeliverySummary | undefined): number | string => (sm ? sm.rejects : '')
const csvNet    = (sm: ReportDeliverySummary | undefined): string =>
  (sm && sm.netKg > 0 ? sm.netKg.toFixed(1) : '')
const csvEst    = (v: number | string | null): string =>
  (typeof v === 'string' ? v : v != null && v > 0 ? v.toFixed(1) : '')
// Matches the printed table's date exactly (2-digit day), so a row in the file
// and the same row on paper are recognisably the same delivery.
const csvDate = (iso: string | null): string =>
  (iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '')

// Who/what/where — the text half of the row.
function csvIdentityCells(d: ReportCsvDelivery, shape: ReportCsvShape): string[] {
  return [
    csvDate(d.received_at),
    d.grower,
    d.variety,
    csvText(d.puc),
    ...(shape.showPoint ? [csvText(d.delivery_point)] : []),
    csvCrate(d.fruit_container_type),
    d.delivery_type === 'EXPORT' ? 'Export' : 'Local',
    csvText(d.vehicle_reg),
    csvText(d.driver_name),
    csvStatus(d.status),
  ]
}

// The counted half. Numbers stay numbers so the spreadsheet re-sums them to the
// printed totals row; only the never-captured cells go out as ''.
function csvCountCells(
  d: ReportCsvDelivery, sm: ReportDeliverySummary | undefined, shape: ReportCsvShape,
  estNetKg: number | string | null = null,
): (string | number)[] {
  return [
    d.bins_received,
    ...shape.sizeCols.map(c => csvSize(sm, c)),
    ...(shape.showSizes ? [csvReject(sm)] : []),
    d.bins_received - d.bins_remaining,
    d.bins_remaining,
    csvNet(sm),
    // An ESTIMATE from a crate-weight sample: its own column, empty (never 0)
    // when the delivery was measured or has no sample, so the measured Net kg
    // column above still re-sums to the measured total.
    ...(shape.showEst ? [csvEst(estNetKg)] : []),
  ]
}

/** Header row for a sheet of this shape. Pair with reportCsvRow — same shape object. */
export function reportCsvHeaders(shape: ReportCsvShape): string[] {
  return [
    'Received', 'Grower', 'Variety', 'PUC',
    ...(shape.showPoint ? ['Point'] : []),
    'Crate type', 'Destination', 'Vehicle', 'Driver', 'Status', 'Crates',
    ...shape.sizeCols,
    ...(shape.showSizes ? ['Reject'] : []),
    'Batched', 'Uncooled', 'Net kg',
    ...(shape.showEst ? ['Est. net kg'] : []),
  ]
}

/** One delivery's cells, in the same order as reportCsvHeaders(shape). */
export function reportCsvRow(
  d: ReportCsvDelivery, sm: ReportDeliverySummary | undefined, shape: ReportCsvShape,
  estNetKg: number | string | null = null,
): (string | number)[] {
  return [...csvIdentityCells(d, shape), ...csvCountCells(d, sm, shape, estNetKg)]
}

// Average GROSS per crate actually recorded at receiving (totals from
// delivery_weighing_summary). Precool's new-dolly form carries the delivery's
// weighed weight over to the cooling step with this: suggested dolly gross =
// avg × crates on the dolly, which also holds for a PARTIAL dolly (a subset of
// the delivery's crates). Null when nothing was weighed — the caller falls back
// to the per-variety config standard. Gross basis matches capture (crate
// included), so both stages deduct the same tare from the same figure.
export function receivingAvgGrossPerCrate(grossKg: number, cratesWeighed: number): number | null {
  if (!(grossKg > 0) || !Number.isFinite(cratesWeighed) || cratesWeighed <= 0) return null
  return grossKg / cratesWeighed
}

/**
 * The gross a staged dolly may carry for a size-split intake — 0 when it must
 * fall back to the per-variety standard.
 *
 * L51: each size row's weight is independently optional, so weighing Small and
 * Medium and leaving Large count-only is an ordinary state. `fruitGross` is
 * then the gross of the WEIGHED crates only, while the dolly is staged with
 * `crate_count` = ALL fruit crates. Handing one to the other spreads a partial
 * gross across the full count, and the batch's derived net (gross − n × tare)
 * comes out understated and misattributed. The delivery itself is unaffected —
 * it commits per-line — so this is only ever about the staged Precool batch.
 *
 * A partially-weighed split is therefore treated like a count-only one: the
 * caller falls back to the variety standard, whose gross does match the crate
 * count it is paired with.
 */
export function splitDollyGross(t: SizeSplitTotals): number {
  if (t.hasInvalid || t.fruitCrates <= 0) return 0
  return t.fruitCratesWeighed === t.fruitCrates ? t.fruitGross : 0
}
